// cSpell:ignore xyflow nodrag nopan animatemotion
import {
  BaseEdge,
  EdgeLabelRenderer,
  getBezierPath,
  getSmoothStepPath,
  getStraightPath,
  Position,
  type Edge,
  type EdgeProps,
} from "@xyflow/react";

import type { Branch } from "../utils/buildGraph";

export type BranchEdgeData = {
  branch?: Branch;
  /** Texte affiché dans la pastille — typiquement "OUI" ou "NON". */
  label?: string;
  /** Algorithme de tracé. Par défaut: smoothstep. */
  path?: "bezier" | "smoothstep" | "step" | "straight";
};

export type BranchEdge = Edge<BranchEdgeData>;

const CHIP_CLASSES: Record<Branch, string> = {
  if: "border-green-300 bg-green-50 text-green-700 dark:border-green-700 dark:bg-green-900/40 dark:text-green-300",
  else: "border-orange-300 bg-orange-50 text-orange-700 dark:border-orange-700 dark:bg-orange-900/40 dark:text-orange-300",
  main: "border-slate-300 bg-slate-50 text-slate-700 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-300",
};

const BRANCH_COLORS: Record<
  Branch,
  { stroke: string; glow: string; particle: string }
> = {
  if: {
    stroke: "#22c55e",
    glow: "rgba(34,197,94,0.35)",
    particle: "#4ade80",
  },
  else: {
    stroke: "#fb923c",
    glow: "rgba(251,146,60,0.35)",
    particle: "#fdba74",
  },
  main: {
    stroke: "#94a3b8",
    glow: "rgba(148,163,184,0.25)",
    particle: "#cbd5e1",
  },
};

// Durée de la particule en secondes selon la longueur approx. de l'edge
function particleDuration(
  sourceX: number,
  sourceY: number,
  targetX: number,
  targetY: number,
): string {
  const dist = Math.hypot(targetX - sourceX, targetY - sourceY);
  const s = Math.max(0.8, Math.min(2.4, dist / 180));
  return `${s.toFixed(2)}s`;
}

export function BranchEdge({
  data = { path: "smoothstep" },
  id,
  markerEnd,
  sourcePosition,
  sourceX,
  sourceY,
  style,
  targetPosition,
  targetX,
  targetY,
}: EdgeProps<BranchEdge>) {
  const [edgePath, labelX, labelY] = getPath({
    type: data.path ?? "smoothstep",
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  const branch = data.branch ?? "main";
  const colors = BRANCH_COLORS[branch];
  const duration = particleDuration(sourceX, sourceY, targetX, targetY);

  return (
    <>
      {/* Trait principal via BaseEdge.
       *  L'animation "draw-on" (strokeDasharray/strokeDashoffset + keyframes
       *  branchEdgeDraw) a été retirée car les valeurs initiales inline
       *  étaient sérialisées telles quelles par modern-screenshot / html-to-image
       *  lors de l'export, masquant complètement les edges dans l'image
       *  générée. La perte est purement cosmétique (600 ms à l'apparition). */}
      <BaseEdge
        id={id}
        path={edgePath}
        markerEnd={markerEnd}
        style={{
          stroke: colors.stroke,
          strokeWidth: 2,
          ...(style as React.CSSProperties | undefined),
        }}
      />

      {/* Particule animée qui circule sur le chemin */}
      <circle r={4} fill={colors.particle}>
        <animateMotion
          dur={duration}
          repeatCount="indefinite"
          calcMode="linear"
        >
          <mpath href={`#edge-path-${id}`} />
        </animateMotion>
      </circle>

      {/* Chemin caché référencé par animateMotion via href */}
      <path id={`edge-path-${id}`} d={edgePath} fill="none" stroke="none" />

      {data.label && (
        <EdgeLabelRenderer>
          <div
            style={{
              transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
            }}
            className={[
              "nodrag nopan",
              "absolute rounded-full border px-2 py-0.5",
              "text-[11px] font-semibold leading-none shadow-sm",
              "pointer-events-auto",
              CHIP_CLASSES[branch],
              "z-100",
            ].join(" ")}
          >
            {data.label}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
}

function getPath({
  type,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
}: {
  type: "bezier" | "smoothstep" | "step" | "straight";
  sourceX: number;
  sourceY: number;
  targetX: number;
  targetY: number;
  sourcePosition: Position;
  targetPosition: Position;
}) {
  switch (type) {
    case "bezier":
      return getBezierPath({
        sourceX,
        sourceY,
        targetX,
        targetY,
        sourcePosition,
        targetPosition,
      });
    case "smoothstep":
      return getSmoothStepPath({
        sourceX,
        sourceY,
        targetX,
        targetY,
        sourcePosition,
        targetPosition,
      });
    case "step":
      return getSmoothStepPath({
        sourceX,
        sourceY,
        targetX,
        targetY,
        sourcePosition,
        targetPosition,
        borderRadius: 0,
      });
    case "straight":
      return getStraightPath({
        sourceX,
        sourceY,
        targetX,
        targetY,
      });
  }
}
